import { gql } from 'graphql-request';
import firebase from 'firebase/app';

import {
  baseApi,
  select,
  insert,
  update,
  afterUpdate,
  afterMultipleUpdate,
  afterInsertSpecSection,
  beforeUpdate,
  objectToStr,
  afterInsert,
  remove,
  afterRemove,
  afterInsertSpecRevisionSection,
} from './base';

import { projectSpecListType, specListType, specRevisionListType, specRevisionSectionType, specScopeType, specSectionCommentType, specSectionDiscussionType, specSectionType, specShowScopeType, specType } from '../graphqlTypes';

const specApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getProjectSpecs: select(builder, 'spec', projectSpecListType, false, {
      filter: { specProjectId: 'spec_project_id' },
    }),
    getSpec: select(builder, 'spec', specType, true),
    getLatestSpecs: select(builder, 'spec', specListType, false, {
      limit: 5,
      order: `{updated_at: desc}`,
    }),
    getSpecs: select(builder, 'spec', specListType, false, {
      filter: { projectId: 'spec_project_id', isMaster: 'is_master', sheetId: 'selection_sheet_id' },
      order: `{is_master: desc, name: asc}`,
    }),
    updateSpec: update(builder, 'spec', specType, beforeUpdate('getSpec')),
    insertSpec: insert(builder, 'spec', specType, afterInsert('getProjectSpecs')),
    
    // Spec Sections API
    getSpecSections: builder.query({
      query: (specId) => ({
        body: gql`
        query {
          spec_section(where: {spec_id: {_eq: ${specId}}}, order_by: { section: asc }) ${specSectionType}
        }
        `,
      }),
      transformResponse: (response) => response.spec_section,
      providesTags: (result, error, arg) => [{ type: 'SpecSection', id: arg }, 'SpecSection'],
    }),
    updateSpecSection: update(
      builder,
      'spec_section',
      specSectionType,
      afterUpdate('getSpecSections'),
      undefined,
      true
    ),
    updateManySpecSections: builder.mutation({
      query: (params) => ({
        body: gql`
        mutation {
          update_spec_section_many (
            updates: [
              ${params.sections
                .map(
                  (r) => `
              {
                where: {id: {_eq: ${r.id}}},
                _set: ${objectToStr(r.payload)}
              }`
                )
                .join(',')}
            ]
          ) {returning ${specSectionType}}
        }`,
      }),
      transformResponse: (response) => response.update_spec_section_many,
      onQueryStarted: afterMultipleUpdate('getSpecSections'),
    }),
    insertSpecSection: insert(builder, 'spec_section', specSectionType, afterInsertSpecSection(), undefined, false, true),
    insertSpecSections: insert(
      builder,
      'spec_section',
      `{returning ${specSectionType}}`,
      afterInsertSpecSection(),
      (result, error, arg) => [{ type: 'SpecSection', id: arg.payload[0].spec_id.toString() }],
      true,
      true
    ),

    // Spec Sections Discussion
    getSpecSuggestion: builder.query({
      query: ({sectionId, suggestionId}) => ({
        body: gql`
        query {
          spec_section_discussion(where: {spec_section_id: {_eq: ${sectionId}}, suggestion_id: { _eq: ${suggestionId} } }, order_by: { created_at: asc }, limit: 1) ${specSectionDiscussionType}
        }
        `,
      }),
      transformResponse: (response) => response.spec_section_discussion?.[0],
      providesTags: (result, error, arg) => [{ type: 'SpecSectionDiscussion', id: arg }, 'SpecSectionDiscussion'],
    }),
    getSpecDiscussions: builder.query({
      query: (sectionId) => ({
        body: gql`
        query {
          spec_section_discussion(where: {spec_section_id: {_eq: ${sectionId}}}, order_by: { created_at: asc }) ${specSectionDiscussionType}
        }
        `,
      }),
      transformResponse: (response) => response.spec_section_discussion,
      providesTags: (result, error, arg) => [{ type: 'SpecSectionDiscussion', id: arg }, 'SpecSectionDiscussion'],
    }),
    updateSpecDiscussion: update(
      builder,
      'spec_section_discussion',
      specSectionDiscussionType,
      afterUpdate('getSpecDiscussions'),
      undefined,
      true
    ),
    insertSpecDiscussion: insert(
      builder, 'spec_section_discussion', specSectionDiscussionType, 
      afterInsertSpecSection('getSpecDiscussions', (data) => data.spec_section_id), 
      undefined, false, true
    ),
    removeSpecDiscussion: remove(
      builder, 'spec_section_discussion', 
      specSectionDiscussionType,
      afterRemove('getSpecDiscussions'),
      ['SpecSectionDiscussion']
    ),

    // Spec Sections Discussion
    getSpecComments: builder.query({
      query: (discussionId) => ({
        body: gql`
        query {
          spec_section_discussion_comment(where: {discussion_id: {_eq: ${discussionId}}}, order_by: { created_at: asc }) ${specSectionCommentType}
        }
        `,
      }),
      transformResponse: (response) => response.spec_section_discussion_comment,
      providesTags: (result, error, arg) => [{ type: 'SpecSectionComment', id: arg }, 'SpecSectionComment'],
    }),

    updateSpecComment: update(
      builder,
      'spec_section_discussion_comment',
      specSectionCommentType,
      afterUpdate('getSpecComments'),
      undefined,
      true
    ),
    insertSpecComment: insert(
      builder, 'spec_section_discussion_comment', specSectionCommentType, 
      afterInsertSpecSection('getSpecComments', (data) => data.discussion_id), 
      undefined, false, true
    ),
    removeSpecComment: remove(
      builder, 'spec_section_discussion_comment', 
      specSectionCommentType,
      afterRemove('getSpecComments'),
      ['SpecSectionComment']
    ),

    // Spec Scope API 

    getSpecScopes: select(builder, 'spec_scope', specScopeType, false, {
      filter: { specId: 'spec_id' },
      order: `{created_at: desc}`, 
    }),
    insertSpecScope: insert(
      builder,
      'spec_scope',
      specScopeType,
      afterInsert('getSpecScopes')
    ),
    updateSpecScope: update(
      builder,
      'spec_scope',
      specScopeType,
      beforeUpdate('getSpecScopes', true)
    ),
    removeSpecScope: remove(
      builder,
      'spec_scope',
      specScopeType,
      afterRemove('getSpecScopes'),
      ['SpecScope']
    ),

    getSpecShowScopes: select(builder, 'spec_show_paragraph', specShowScopeType, false, {
      filter: { specId: 'spec_id' }
    }),
    insertSpecShowScope: insert(
      builder,
      'spec_show_paragraph',
      specShowScopeType,
      afterInsert('getSpecShowScopes'),
    ),
    insertSpecShowScopes: insert(
      builder,
      'spec_show_paragraph',
      `{returning ${specShowScopeType}}`,
      afterInsertSpecSection('getSpecShowScopes'),
      (result, error, arg) => [{ type: 'SpecShowScope', id: arg.payload[0].spec_id.toString() }],
      true,
      true
    ),
    removeSpecShowScope: remove(
      builder,
      'spec_show_paragraph',
      specShowScopeType,
      afterRemove('getSpecShowScopes'),
      ['SpecShowScope']
    ),

    // Spec Revisions API 
    getSpecRevisions: select(builder, 'spec_revision', specRevisionListType, false, {
      filter: { specId: 'spec_id' },
      order: `{created_at: desc}`,
    }),
    insertSpecRevision: insert(
      builder,
      'spec_revision',
      specRevisionListType,
      afterInsert('getSpecRevisions'),
      (result, error, arg) => [{ type: 'SpecRevision', id: arg.payload.spec_id.toString() }]
    ),
    updateSpecRevision: update(
      builder,
      'spec_revision',
      specRevisionListType,
      beforeUpdate('getSpecRevisions', true)
    ),
    removeSpectRevision: remove(
      builder,
      'spec_revision',
      specRevisionListType,
      afterRemove('getSpecRevisions'),
      ['SpecRevision']
    ),
    getSpecRevisionSections: builder.query({
      query: (revisionId) => ({
        body: gql`
        query {
          spec_revision_section(where: {spec_revision_id: {_eq: ${revisionId}}}) ${specRevisionSectionType}
        }
        `,
      }),
      transformResponse: (response) => response.spec_section,
      providesTags: (result, error, arg) => [{ type: 'SpecRevisionSection', id: arg }, 'SpecRevisionSection'],
    }),
    insertSpecRevisionSections: insert(
      builder,
      'spec_revision_section',
      `{returning ${specRevisionSectionType}}`,
      afterInsertSpecRevisionSection(),
      (result, error, arg) => [{ type: 'SpecRevisionSection', id: arg.payload[0].spec_revision_id.toString() }],
      true,
      true
    ),
  }),
  overrideExisting: false,
});

export const {
  useGetProjectSpecsQuery,
  useGetSpecQuery,
  useGetLatestSpecsQuery,
  useGetSpecsQuery,

  useGetSpecScopesQuery,
  useInsertSpecScopeMutation,
  useUpdateSpecScopeMutation,
  useRemoveSpecScopeMutation,
  useGetSpecShowScopesQuery,
  useInsertSpecShowScopeMutation,
  useInsertSpecShowScopesMutation,
  useRemoveSpecShowScopeMutation,

  useUpdateSpecMutation,
  useInsertSpecMutation,

  useGetSpecSectionsQuery,
  useGetSpecSuggestionQuery,
  useUpdateSpecSectionMutation,
  useUpdateManySpecSectionsMutation,
  useInsertSpecSectionMutation,
  useInsertSpecSectionsMutation,

  useGetSpecDiscussionsQuery,
  useInsertSpecDiscussionMutation,
  useUpdateSpecDiscussionMutation,
  useRemoveSpecDiscussionMutation,

  useGetSpecCommentsQuery,
  useInsertSpecCommentMutation,
  useUpdateSpecCommentMutation,
  useRemoveSpecCommentMutation,

  useGetSpecRevisionsQuery,
  useInsertSpecRevisionMutation,
  useUpdateSpecRevisionMutation,
  useRemoveSpectRevisionMutation,
  useGetSpecRevisionSectionsQuery,
  useInsertSpecRevisionSectionsMutation,
} = specApi;
